import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { services, serviceContent } from '../data/services';
import { Button } from './ui/button';
import { 
  ArrowLeft, 
  Check, 
  Phone, 
  ChevronRight,
  Container,
  Recycle,
  ShieldCheck,
  Mountain,
  Droplets,
  Factory
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const iconMap = {
  Container,
  Recycle,
  ShieldCheck,
  Mountain,
  Droplets,
  Factory
};

const ServicePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const serviceIndex = services.findIndex(s => s.slug === slug);
  
  if (serviceIndex === -1) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#1a365d] mb-4">Service Not Found</h1>
          <Button onClick={() => navigate('/services')}>View All Services</Button>
        </div>
      </div>
    );
  }

  const service = services[serviceIndex];
  const content = serviceContent[slug];
  const Icon = iconMap[service.icon];
  const otherServices = services.filter((_, idx) => idx !== serviceIndex).slice(0, 3);

  const scrollToContact = () => {
    const el = document.querySelector('#contact-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={service.heroImage}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#1a365d]/85 to-[#1a365d]/70"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <div className="flex items-center space-x-2 text-white/70 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/services" className="hover:text-white transition-colors">Services</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">{service.title}</span>
          </div>

          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Icon className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white">
              {service.title}
            </h1>
          </div>

          <p className="text-xl text-white/80 max-w-2xl mb-8">
            {service.description}
          </p>

          <div className="flex flex-wrap gap-4">
            <Button
              onClick={scrollToContact}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8 py-4 rounded-lg"
            >
              Get a Quote
            </Button>
            <a
              href="tel:(905) 595-6987"
              className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg hover:bg-white/20 transition-colors"
            >
              <Phone className="h-5 w-5" />
              <span>(905) 595-6987</span>
            </a>
          </div>
        </div>
      </section>

      {/* Highlights Bar */}
      <section className="bg-[#1a365d] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.highlights.map((h, i) => (
              <div key={i} className="flex items-center space-x-3 text-white">
                <div className="w-8 h-8 bg-blue-500/30 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="h-4 w-4 text-blue-300" />
                </div>
                <span className="text-sm">{h}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1a365d] mb-4">
              {service.headline}
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {content.features.map((f, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 bg-[#1a365d] rounded-xl flex items-center justify-center mb-6">
                  <span className="text-white font-bold text-lg">{i + 1}</span>
                </div>
                <h3 className="text-xl font-bold text-[#1a365d] mb-4">{f.title}</h3>
                <p className="text-gray-600 mb-6">{f.description}</p>
                <ul className="space-y-2">
                  {f.details.map((detail, idx) => (
                    <li key={idx} className="flex items-center space-x-2 text-sm text-gray-500">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold tracking-wider uppercase text-sm">Our Process</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1a365d] mt-4">
              How It Works
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {content.process.map((s, i) => (
              <div key={i} className="relative">
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-[#1a365d] rounded-full flex items-center justify-center text-white font-bold mb-4">
                    {s.step}
                  </div>
                  <h3 className="text-lg font-bold text-[#1a365d] mb-2">{s.title}</h3>
                  <p className="text-gray-600 text-sm">{s.description}</p>
                </div>
                {i < content.process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ChevronRight className="h-6 w-6 text-blue-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      {service.startingPrice && (
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-[#1a365d] to-[#2d5a9e] rounded-3xl p-8 md:p-12 text-center text-white">
              <h2 className="text-3xl font-bold mb-4">Transparent Pricing</h2>
              <div className="flex items-baseline justify-center space-x-2 mb-4">
                <span className="text-lg opacity-80">Starting at</span>
                <span className="text-5xl font-bold">${service.startingPrice}</span>
              </div>
              <p className="text-white/80 mb-8">Contact us for a detailed quote based on your specific needs.</p>
              <Button
                onClick={scrollToContact}
                className="bg-white text-[#1a365d] hover:bg-blue-50 font-semibold px-8 py-4 rounded-lg"
              >
                Request a Quote
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1a365d]">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {content.faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                  <span className="font-medium text-[#1a365d]">{faq.q}</span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-gray-600">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact CTA */}
      <section id="contact-section" className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 shadow-lg">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[#1a365d] mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-gray-600">
                Contact us today for a free quote on {service.title.toLowerCase()} services.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <a
                href="tel:(905) 595-6987"
                className="flex items-center justify-center space-x-3 bg-[#1a365d] text-white font-semibold px-8 py-4 rounded-xl hover:bg-[#2d4a7c] transition-colors"
              >
                <Phone className="h-5 w-5" />
                <span>Call (905) 595-6987</span>
              </a>
              <a
                href="mailto:info@renewedenv.com"
                className="flex items-center justify-center space-x-3 bg-gray-100 text-[#1a365d] font-semibold px-8 py-4 rounded-xl hover:bg-gray-200 transition-colors"
              >
                <span>Email Us</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1a365d]">Explore Other Services</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {otherServices.map((s) => {
              const ServiceIcon = iconMap[s.icon];
              return (
                <Link
                  key={s.id}
                  to={`/services/${s.slug}`}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
                >
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#1a365d] transition-colors">
                    <ServiceIcon className="h-6 w-6 text-[#1a365d] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1a365d] mb-2">{s.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{s.shortDescription}</p>
                  <span className="text-blue-600 text-sm font-medium flex items-center">
                    Learn more <ChevronRight className="h-4 w-4 ml-1" />
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/services"
              className="inline-flex items-center text-[#1a365d] font-semibold hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              View All Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
